import { FileRepository } from "@/components/features";
import { useUser } from "@/components/UserProvider";
import { AppPageShell, Card, CardContent, Typography } from "@/components/ui";
import LawWorksLoader from "@/components/ui/LawWorksLoader/LawWorksLoader";
import { retrieveMassTortQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import { retrieveMatterFilesQueryOptions } from "@/lib/data/react-query/matters/matter-file";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

export const Route = createFileRoute(
  "/_auth/mass-torts/$massTortSearchId/_layout/files",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { massTortSearchId: massTortId } = Route.useParams();
  const { hasPagePermission } = useUser();
  const { data: massTortResponse, isLoading: isMassTortLoading } = useQuery({
    ...retrieveMassTortQueryOptions(massTortId),
  });
  const { data: massTortFilesResponse, isLoading: isMassTortFilesLoading } =
    useQuery({
      ...retrieveMatterFilesQueryOptions({ massTortId }),
      enabled: Boolean(massTortId),
    });

  const massTort = massTortResponse?.data;
  const massTortWithFiles = useMemo(
    () =>
      massTort && massTortId
        ? {
            ...massTort,
            matterFile: (massTortFilesResponse?.data ?? massTort.files).map(
              (file) => ({
                ...file,
                massTortId,
              }),
            ),
          }
        : undefined,
    [massTort, massTortFilesResponse?.data, massTortId],
  );
  const isPageLoading = Boolean(
    massTortId && (isMassTortLoading || isMassTortFilesLoading),
  );

  if (isPageLoading) {
    return (
      <AppPageShell padding="none" className="h-full min-h-0 overflow-hidden">
        <LawWorksLoader className="w-full" />
      </AppPageShell>
    );
  }

  if (!hasPagePermission(PAGE_PERMISSION_KEYS.massTortsDetailFiles)) {
    return (
      <div className="grid h-screen w-full grid-cols-1 gap-4 p-6">
        <Card className="flex h-full flex-col">
          <CardContent className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <Typography variant="subHeading">Mass tort files</Typography>
            <Typography className="text-muted-foreground">
              You do not have permission to view files.
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!massTortId || !massTortWithFiles) {
    return (
      <div className="grid h-screen w-full grid-cols-1 gap-4 p-6">
        <Card className="flex h-full flex-col">
          <CardContent className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <Typography variant="subHeading">Mass tort files</Typography>
            <Typography className="text-muted-foreground">
              Unable to load files for this mass tort.
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AppPageShell padding="none" className="h-full min-h-0 overflow-hidden">
      <div className="h-full min-h-0 overflow-hidden">
        <FileRepository
          massTort={massTortWithFiles}
          showShareFolders={false}
          compactPreviewLayout
        />
      </div>
    </AppPageShell>
  );
}
